import React from "react";
import { Button } from "@/common/ui/Button";
import { H2, LightText2, H3 } from "@/common/ui/Text";
import { X } from "lucide-react";
import moment from "moment";
import { formatCurrency } from "@/lib/utils";
import { PaymentStatusreceived, PaymentStatusWaiting } from "@/assets/Svg";
import { useTransactionDetails } from "@/hooks/useTransactionDetails";
import { useSelector } from "react-redux";
import { loginSelector } from "@/store/features/loginSlice";
import { PERMISSIONS } from "@/utils/constant/global.constant";
import { isHasPermission } from "@/utils/helper/permissions";
import { motion } from "framer-motion";

interface ViewTransactionModalProps {
  transactionId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onDownloadReceipt: (transactionId: string) => void;
  isDownloading?: boolean;
}

const ViewTransactionModal: React.FC<ViewTransactionModalProps> = ({
  transactionId,
  isOpen,
  onClose,
  onDownloadReceipt,
  isDownloading = false,
}) => {
  const { currentUser } = useSelector(loginSelector);
  const { transactionDetails, isLoading, error, refreshDetails } = useTransactionDetails(transactionId);
  
  // Permission check for download receipt
  const canDownloadReceipt = isHasPermission(currentUser?.perms, PERMISSIONS.PAYMENT["Download Receipt"]);
  
  if (!isOpen || !transactionId) return null;

  // Show loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div className="fixed inset-0 bg-gray-500 opacity-75 transition-opacity" onClick={onClose}></div>
        <div className="fixed right-0 top-0 h-full rounded-tl-[10px] rounded-bl-[10px] w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-[16px] py-[9px]">
              <H2 text="View transaction" />
              <Button
                variant="transparent"
                size="small"
                onClick={onClose}
                className="!p-0 !min-w-fit"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <motion.div
                  className="w-16 h-16 rounded-full mx-auto mb-4"
                  style={{
                    background: "conic-gradient(from 0deg, #FDF7FF, #FDF7FF, #FDF7FF, #C59EF7, #9A6AD4, #6F36BC)",
                    maskImage: "radial-gradient(transparent 55%, black 55%)",
                    WebkitMaskImage: "radial-gradient(transparent 55%, black 55%)",
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <LightText2 text="Loading transaction details..." className="text-gray-500" />
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div className="fixed inset-0 bg-gray-500 opacity-75 transition-opacity" onClick={onClose}></div>
        <div className="fixed right-0 top-0 h-full rounded-tl-[10px] rounded-bl-[10px] w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-[16px] py-[9px]">
              <H2 text="View transaction" />
              <Button
                variant="transparent"
                size="small"
                onClick={onClose}
                className="!p-0 !min-w-fit"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="mb-1">
                  <img src="/icons/error-state.svg" alt="Error" className="mx-auto w-[150px] h-[100px]" />
                </div>
                <div className="flex flex-col items-center justify-center">
                  <LightText2 text="Something went wrong" className="text-lg font-semibold text-gray-800 mb-1" />
                  <Button variant="outline" onClick={refreshDetails} className="mt-2">
                    Try Again
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show no data state
  if (!transactionDetails) {
    return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div className="fixed inset-0 bg-gray-500 opacity-75 transition-opacity" onClick={onClose}></div>
        <div className="fixed right-0 top-0 h-full rounded-tl-[10px] rounded-bl-[10px] w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-[16px] py-[9px]">
              <H2 text="View transaction" />
              <Button
                variant="transparent"
                size="small"
                onClick={onClose}
                className="!p-0 !min-w-fit"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-gray-400 mb-4">📄</div>
                <LightText2 text="Transaction not found" className="text-gray-600" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const transaction = transactionDetails;


  const formatDate = (dateString: string) => {
    return moment(dateString).format("DD MMM YYYY, hh:mm:ss A");
  };

  const getStatusConfig = (status: string) => {
    const statusConfig = {
      Paid: { 
        bg: "bg-green-100", 
        text: "text-green-800", 
        label: "Received", 
        icon: "✓",
        amountColor: "text-[#04844B]",
        iconBg: "bg-[#04844B0D]",
        svg: PaymentStatusreceived,
        message: "Payment received on",
        headerText: "Payment Received"
      },
      Pending: { 
        bg: "bg-yellow-100", 
        text: "text-yellow-800", 
        label: "Waiting", 
        icon: "⏳",
        amountColor: "text-[#FFA800]",
        iconBg: "bg-[#FFA8000D]",
        svg: PaymentStatusWaiting,
        message: "Payment pending since",
        headerText: "Payment Waiting"
      },
      Expired: { 
        bg: "bg-gray-100", 
        text: "text-gray-800", 
        label: "Expired", 
        icon: "⏰",
        amountColor: "text-gray-600",
        iconBg: "bg-gray-100",
        svg: null,
        message: "Payment expired on",
        headerText: "Payment Expired"
      },
    };

    // Map transaction statuses to config keys
    const statusMapping: { [key: string]: keyof typeof statusConfig } = {
      'Paid': 'Paid',
      'Created': 'Pending',
      'Expired': 'Expired'
    };

    const configKey = statusMapping[status] || 'Pending';
    return statusConfig[configKey];
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-500 opacity-75 transition-opacity" onClick={onClose}></div>
      
      {/* Right Panel */}
      <div className="fixed right-0 top-0 h-full rounded-tl-[10px] rounded-bl-[10px] w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-[16px] py-[9px] ">
            <H2 text={"View transaction"} className="!text-[16px]" />
            <Button
              variant="transparent"
              size="small"
              onClick={onClose}
              className="!p-0 !min-w-fit"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {/* Payment Received Section */}
              <div className="flex flex-col items-center">
                {(() => {
                  const statusConfig = getStatusConfig(transaction.status);
                  return (
                    <>
                      <div className={`flex items-center justify-center w-6 h-6 ${statusConfig.iconBg} rounded mb-2`}>
                        {statusConfig.svg ? (
                          typeof statusConfig.svg === 'function' && statusConfig.svg()
                        ) : (
                          <svg className={`w-3.5 h-3.5 ${statusConfig.amountColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {transaction.status === 'Failed' ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            )}
                          </svg>
                        )}
                      </div>
                      <div className={`text-3xl font-bold ${statusConfig.amountColor} mb-1`}>
                        {formatCurrency(transaction.document_price, transaction.currency || "INR")}
                      </div>
                      <div className="text-base text-gray-700">{statusConfig.message}</div>
                      <div className="text-sm text-[#ADB1C2]">{transaction.received_on ? formatDate(transaction.received_on) : "N/A"}</div>
                    </>
                  );
                })()}
              </div>

              {/* Payment Details Card */}
              <div className="border border-[#F4F4F4] rounded-[10px] overflow-hidden">
                <div className="px-[16px] py-[14px] border-b border-[#F4F4F4] bg-white">
                  <H3 text="Payment details" className="text-base font-bold text-gray-700" />
                </div>
                <div className="bg-white pb-[8px]">
                  <div className="px-4 py-2">
                    <div className="flex flex-col items-start gap-[3px]">
                      <span className="text-[13px] font-[700] text-gray-600">Proof ID</span>
                      <span className="text-[13px] font-[400] text-[#6F36BC]">{transaction.proof_id}</span>
                    </div>
                  </div>
                  <div className="px-4 py-2">
                    <div className="flex flex-col items-start gap-[3px]">
                      <span className="text-[13px] font-[700] text-gray-600">Paid by</span>
                      <span className="text-[13px] font-[400] text-gray-700">
                        {transaction.status === "Paid" ? (transaction.paid_by || "-") : "-"}
                      </span>
                    </div>
                  </div>
                  <div className="px-4 py-2">
                    <div className="flex flex-col items-start gap-[3px]">
                      <span className="text-[13px] font-[700] text-gray-600">Transaction ID</span>
                      <span className="text-[13px] font-[400] text-gray-700">{transaction.paid_by ? transaction.transaction_id : "-"}</span>
                    </div>
                  </div>
                  <div className="px-4 py-2">
                    <div className="flex flex-col items-start gap-[3px]">
                      <span className="text-[13px] font-[700] text-gray-600">Document price</span>
                      <span className="text-[13px] font-[400] text-gray-700">{formatCurrency(transaction.document_price, transaction.currency || "INR")}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Employee Details Card */}
              <div className="border border-[#F4F4F4] rounded-[10px] overflow-hidden">
                <div className="!px-[16px] !py-[14px] border-b border-[#F4F4F4] bg-white">
                  <H3 text="Employee details" className="text-base font-[700] text-gray-700" />
                </div>
                <div className="bg-white pb-[10px]">
                  <div className="px-4 py-2">
                    <div className="flex flex-col items-start gap-[3px]">
                      <span className="text-[13px] font-[700] text-gray-600">Employee ID</span>
                      <span className="text-[13px] font-[400] text-[#6F36BC]">{transaction.emp_id}</span>
                    </div>
                  </div>
                  <div className="px-4 py-2">
                    <div className="flex flex-col items-start gap-[8px]">
                      <span className="text-[13px] font-[700] text-gray-600">Employee name</span>
                      <div className="flex items-center gap-1">
                        {transaction.empName?.profilePic ? (
                          <img
                            src={transaction.empName.profilePic}
                            alt={transaction.empName.label || transaction.employee_name}
                            className="h-5 w-5 rounded-full object-cover object-center"
                            style={{ minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }}
                          />
                        ) : (
                          <div className="flex w-5 h-5 !min-h-5 !min-w-5 items-center justify-center border border-text-primary rounded-full text-text-primary font-[400] text-[10px]">
                            {transaction.empName?.profileText || (transaction.employee_name || "Unknown").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="text-sm text-gray-700">
                          {transaction.empName?.label || transaction.employee_name || "Unknown"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer */}

          {
            transaction.status === 'Paid' && canDownloadReceipt && (
              <div className="border-t border-gray-200 p-[16px] flex justify-end">
                <Button
                  variant="primary"
                  size="medium"
                  onClick={() => onDownloadReceipt(transaction.transaction_id)}
                  disabled={isDownloading}
                  className="flex min-w-[72px] px-[16px] !py-[6px] justify-center items-center gap-1.5"
                >
                  {isDownloading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <p className="text-[13px] font-[400]">
                      Download receipt
                    </p>
                  )}
                </Button>
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
};

export default ViewTransactionModal;
